import React from 'react';
import { format } from 'date-fns';
import {
  CalendarIcon,
  ClockIcon,
  UserIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { useCurrency } from '../../contexts/CurrencyContext';

const BookingSummary = ({ service, date, timeSlot, clientInfo }) => {
  const { formatPrice } = useCurrency();
  if (!service && !date && !timeSlot) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 sticky top-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Booking Summary</h2>

      <div className="space-y-4">
        {/* Service */}
        {service && (
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <UserIcon className="w-5 h-5 text-primary-600" />
              </div>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-500">Service</p>
              <p className="text-base font-semibold text-gray-900 mt-1">
                {service.name}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {service.duration} minutes
              </p>
            </div>
          </div>
        )}

        {/* Date */}
        {date && (
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <CalendarIcon className="w-5 h-5 text-primary-600" />
              </div>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-500">Date</p>
              <p className="text-base font-semibold text-gray-900 mt-1">
                {format(date, 'EEEE, MMMM d, yyyy')}
              </p>
            </div>
          </div>
        )}

        {/* Time */}
        {timeSlot && (
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <ClockIcon className="w-5 h-5 text-primary-600" />
              </div>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-500">Time</p>
              <p className="text-base font-semibold text-gray-900 mt-1">
                {format(new Date(timeSlot.startTime), 'h:mm a')} -{' '}
                {format(new Date(timeSlot.endTime), 'h:mm a')}
              </p>
            </div>
          </div>
        )}

        {/* Client Info */}
        {clientInfo && (clientInfo.firstName || clientInfo.email) && (
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <UserIcon className="w-5 h-5 text-primary-600" />
              </div>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-500">Client</p>
              {clientInfo.firstName && clientInfo.lastName && (
                <p className="text-base font-semibold text-gray-900 mt-1">
                  {clientInfo.firstName} {clientInfo.lastName}
                </p>
              )}
              {clientInfo.email && (
                <p className="text-sm text-gray-600 mt-1">{clientInfo.email}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Price */}
      {service && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CurrencyDollarIcon className="w-5 h-5 text-gray-400 mr-2" />
              <span className="text-sm font-medium text-gray-600">Total</span>
            </div>
            <span className="text-2xl font-bold text-gray-900">{formatPrice(service.price)}</span>
          </div>
        </div>
      )}

      {/* Progress Indicator */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <span className={service ? 'text-primary-600 font-medium' : 'text-gray-400'}>
            1. Service
          </span>
          <span className={date ? 'text-primary-600 font-medium' : 'text-gray-400'}>
            2. Date
          </span>
          <span className={timeSlot ? 'text-primary-600 font-medium' : 'text-gray-400'}>
            3. Time
          </span>
          <span className={clientInfo?.firstName ? 'text-primary-600 font-medium' : 'text-gray-400'}>
            4. Info
          </span>
        </div>
        <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-500 transition-all duration-500"
            style={{
              width: `${
                (!service ? 0 : !date ? 25 : !timeSlot ? 50 : !clientInfo?.firstName ? 75 : 100)
              }%`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default BookingSummary;
